import axios from "axios";
import store from "../store";
import { logoutUser } from "../store/auth/login/actions";
import { CONTACT_IMPORTS } from "./url_helper";

// Base URL — REACT_APP_SERVER_URL ends in a slash (e.g. https://edge.bongasoft.com/).
// All Bongasoft APIs live under /api/ on the single edge gateway.
const API_URL = process.env.REACT_APP_SERVER_URL + "api/";

// KenGen is a single-country (Kenya) tenant; billing + some reads require a country.
export const COUNTRY = process.env.REACT_APP_COUNTRY || "KE";

const axiosApi = axios.create({
  baseURL: API_URL,
});

// Inject the bearer token from Redux on every request.
axiosApi.interceptors.request.use(function (config) {
  // Prefer Redux, fall back to localStorage (survives a refresh before rehydrate).
  const token = store.getState().Login.sessionToken || localStorage.getItem("sessionToken");
  if (token) config.headers.Authorization = "Bearer " + token;
  return config;
});

// Response interceptor:
//  - 401/403 → clear session and hard-redirect to /login (refresh is handled in route.js).
//  - 402     → insufficient SMS units; attach the flat billing envelope to the error.
axiosApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    // Calls pass { skipAuthLogout: true } to treat a 401/403 as "feature
    // unavailable" instead of ending the session (MFA, API keys).
    const skipAuthLogout = error?.config?.skipAuthLogout;

    if ((status === 401 || status === 403) && !skipAuthLogout) {
      localStorage.clear();
      store.dispatch(
        logoutUser(() => {
          window.location.href = "/login";
        })
      );
    }

    if (status === 402) {
      // { success:false, error, have, need, currency, message }
      error.insufficientUnits = error?.response?.data;
    }

    return Promise.reject(error);
  }
);

// Bongasoft uses two response envelopes:
//  - user / contacts / billing → { success, message, data }   → unwrap to `data`
//  - messaging                 → raw DTOs / Spring Page        → pass through
//  - flat 402                  → { success:false, error, ... } → pass through (no `data`)
export function unwrapEnvelope(payload) {
  // Wrapped services use { success, message, data } (user/billing) OR
  // { status, message, data } (contacts) — unwrap to `data` for both.
  if (
    payload &&
    typeof payload === "object" &&
    "data" in payload &&
    ("success" in payload || "status" in payload)
  ) {
    return payload.data;
  }
  return payload;
}

// Normalize a Spring Page<T> into the shape the tables consume.
export function unwrapPage(page) {
  if (Array.isArray(page)) return { rows: page, total: page.length, pages: 1, page: 0 };
  return {
    rows: page?.content ?? [],
    total: page?.totalElements ?? 0,
    pages: page?.totalPages ?? 0,
    page: page?.number ?? 0,
  };
}

export async function get(url, config = {}) {
  return await axiosApi.get(url, { ...config }).then((r) => unwrapEnvelope(r?.data));
}

export async function post(url, data, config = {}) {
  return axiosApi.post(url, { ...data }, { ...config }).then((r) => unwrapEnvelope(r?.data));
}

export async function put(url, data, config = {}) {
  return axiosApi.put(url, { ...data }, { ...config }).then((r) => unwrapEnvelope(r?.data));
}

export async function patch(url, data, config = {}) {
  return axiosApi.patch(url, { ...data }, { ...config }).then((r) => unwrapEnvelope(r?.data));
}

export async function del(url, config = {}) {
  return await axiosApi.delete(url, { ...config }).then((r) => unwrapEnvelope(r?.data));
}

// Raw axios instance — for multipart uploads, blob downloads, and query-param posts
// where the JSON-body wrapping above doesn't fit.
export { axiosApi };

// ─────────────────────── user-service: sub-users (Phase 4) ───────────────────────
// Split a single "name" field into Bonga's firstName / lastName.
function splitName(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") };
}

// Bonga validates phones as E.164 (^\+?[1-9]\d{1,14}$), but this portal has always
// collected them in Kenyan local form (0712…). Convert before sending.
export function toE164(phone = "", dialCode = "254") {
  const digits = String(phone).trim().replace(/[^\d+]/g, "");
  if (!digits) return "";
  if (digits.startsWith("+")) return digits;
  if (digits.startsWith("0")) return `+${dialCode}${digits.slice(1)}`;
  if (digits.startsWith(dialCode)) return `+${digits}`;
  return `+${dialCode}${digits}`;
}

// Pull the server's message out of an axios error — the API returns
// { success, message } and the raw axios message ("Request failed with status
// code 400") is useless to the user.
export function apiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  const data = error?.response?.data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  const fieldErrors = data?.errors || data?.data;
  if (fieldErrors && typeof fieldErrors === "object") {
    const first = Object.values(fieldErrors).find((v) => typeof v === "string");
    if (first) return first;
  }
  return fallback;
}

// Admin-only sub-user/assignment calls opt out of the global auto-logout — a 403
// here means "not an admin", not "session dead".
const ADMIN_OPT = { skipAuthLogout: true };

// Create a sub-user. POST /api/companies/{companyId}/users/create
export const createChildUser = (companyId, userData) => {
  const split = splitName(userData.name);
  const department = (userData.department ?? userData.location ?? "").trim();
  return post(`companies/${companyId}/users/create`, {
    email: userData.email,
    phone: toE164(userData.phone),
    firstName: userData.firstName || split.firstName,
    lastName: userData.lastName || split.lastName,
    department: department || undefined,
    isCompanyAdmin: String(userData.role_id) === "1",
  }, ADMIN_OPT);
};

// Edit a sub-user's profile fields. PUT /api/users/{id}
export const editChildUser = (userId, userData) => {
  const split = splitName(userData.name);
  const department = userData.department ?? userData.location;
  return put(`users/${userId}`, {
    firstName: userData.firstName || split.firstName,
    lastName: userData.lastName || split.lastName,
    // Omitted when untouched — a phone change stages an SMS OTP, and legacy rows hold
    // local-format numbers that would look "changed" the moment we normalise them.
    phone: userData.phone === undefined ? undefined : toE164(userData.phone),
    // undefined = leave unchanged; "" = clear.
    department: department === undefined ? undefined : String(department).trim(),
  }, ADMIN_OPT);
};

// Detach a sub-user from the company. DELETE /api/companies/{companyId}/users/{userId}
export const deleteChildUser = (companyId, userId) =>
  del(`companies/${companyId}/users/${userId}`, ADMIN_OPT);

// Edit own profile. PUT /api/users/{id} (fixes the legacy /api/api/editprofile bug).
export const editUserProfile = (userId, data) => {
  const split = splitName(data.name);
  return put(`users/${userId}`, {
    firstName: data.firstName || split.firstName,
    lastName: data.lastName || split.lastName,
    phone: data.phone,
  });
};

// ─────────────────────── contacts-service (Phase 2) ───────────────────────
// Trigger a CSV blob download in the browser.
function downloadBlob(data, filename) {
  const href = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Rename / edit a list. PUT /api/contacts/lists/{id}
export const updateContactList = (id, data) =>
  put(`contacts/lists/${id}`, { name: data.name, description: data.description });

// DELETE /api/contacts/lists/{id}
export const deleteContactList = (id) => del(`contacts/lists/${id}`);

// GET /api/contacts/lists/{id} — single list (for its name/meta).
export const getContactListById = (id) => get(`contacts/lists/${id}`);

// Update a single contact. PUT /api/contacts/{id}
export const updateContact = (contactId, data) =>
  put(`contacts/${contactId}`, {
    firstName: data.first_name ?? data.firstName,
    lastName: data.last_name ?? data.lastName,
    mobile: data.msisdn ?? data.mobile,
    email: data.email || undefined,
    contactType: data.contactType || undefined,
  });

// Export ALL contacts → CSV. POST /api/contacts/exports {scope:ALL}
export const exportContactList = async () => {
  const res = await axiosApi.post(
    "contacts/exports",
    { scope: "ALL", format: "CSV" },
    { responseType: "blob" }
  );
  downloadBlob(res.data, "contact_list_export.csv");
};

// Export ONE list → CSV blob. POST /api/contacts/exports {scope:LIST,listId}
export const exportContactsByListId = async (listId) => {
  const res = await axiosApi.post(
    "contacts/exports",
    { scope: "LIST", listId: Number(listId), format: "CSV" },
    { responseType: "blob" }
  );
  return res.data;
};

// ───────────── contacts-service: create a list from an uploaded file ─────────────
// Import wizard: upload → mapping (returns a preview summary) → commit (async job).
// Split into steps so the page can ask about contacts that already exist before
// committing. Mapping is idempotent — re-post it to refresh the summary.

// Large files: parsing + preview can take a while server-side.
const IMPORT_TIMEOUT_MS = 120000;

// Thrown for problems we can explain without the server (e.g. no Mobile column).
export class ImportError extends Error {
  constructor(message) {
    super(message);
    this.name = "ImportError";
    this.userMessage = message;
  }
}

// The upload returns suggestedMapping as { header: "fieldName" }; the mapping
// endpoint expects { header: { type: "contact", field } }. Two headers can
// suggest the same field ("Phone" + "Mobile" → mobile) and the server rejects
// duplicates — first column wins, later ones are ignored.
export function buildImportMapping(suggestedMapping = {}) {
  const mapping = {};
  const used = new Set();
  Object.entries(suggestedMapping || {}).forEach(([header, field]) => {
    if (!field || field === "ignore" || used.has(field)) {
      mapping[header] = { type: "ignore" };
      return;
    }
    used.add(field);
    mapping[header] = { type: "contact", field };
  });
  return { mapping, hasMobile: used.has("mobile") };
}

const postImportMapping = async ({ sessionId, mapping, listName }, addExistingToList) =>
  unwrapEnvelope(
    (await axiosApi.post(
      `${CONTACT_IMPORTS}/${sessionId}/mapping`,
      { mapping, listName, skipDuplicates: true, addExistingToList },
      { timeout: IMPORT_TIMEOUT_MS }
    )).data
  );

// Cancel a pre-commit session. Best-effort — a stale session expires on its own.
export const cancelContactImport = async (sessionId) => {
  if (!sessionId) return;
  try {
    await axiosApi.post(`${CONTACT_IMPORTS}/${sessionId}/cancel`);
  } catch (error) {
    console.error("Failed to cancel import session:", error);
  }
};

// Step 1: upload the file and map it under a new list name (existing contacts
// NOT added yet). Returns { sessionId, mapping, listName, summary } — pass it
// back to the calls below.
export const startContactListImport = async (file, listName) => {
  const form = new FormData();
  form.append("file", file);
  form.append("regionIso2", COUNTRY);
  const parsed = unwrapEnvelope(
    (await axiosApi.post(CONTACT_IMPORTS, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: IMPORT_TIMEOUT_MS,
    })).data
  );
  const sessionId = parsed?.sessionId;
  const { mapping, hasMobile } = buildImportMapping(parsed?.suggestedMapping);
  if (!hasMobile) {
    await cancelContactImport(sessionId);
    throw new ImportError(
      "We couldn't find a phone number column in this file. Add a column named \"Mobile\" or \"Phone\" with the contacts' numbers and try again."
    );
  }
  const ctx = { sessionId, mapping, listName };
  try {
    const session = await postImportMapping(ctx, false);
    return { ...ctx, summary: session?.previewSummary || {} };
  } catch (error) {
    await cancelContactImport(sessionId);
    throw error;
  }
};

// Step 2 (optional): toggle whether existing contacts are added to the list.
// Returns the refreshed preview summary.
export const setImportAddExisting = async (ctx, addExistingToList) => {
  const session = await postImportMapping(ctx, addExistingToList);
  return session?.previewSummary || {};
};

// Step 3: start the async import job. Poll GET contacts/imports/{id} for status.
export const commitContactImport = async (sessionId) =>
  unwrapEnvelope((await axiosApi.post(`${CONTACT_IMPORTS}/${sessionId}/commit`)).data);

// Step 4: wait for the async job to finish. Returns the session once it reaches
// IMPORTED / FAILED / CANCELLED, or the last seen state (still IMPORTING) after
// the timeout — very large files keep running in the background.
const IMPORT_DONE = ["IMPORTED", "FAILED", "CANCELLED"];
export const waitForContactImport = async (sessionId, timeoutMs = 90000) => {
  const deadline = Date.now() + timeoutMs;
  let session = null;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 1500));
    session = unwrapEnvelope((await axiosApi.get(`${CONTACT_IMPORTS}/${sessionId}`)).data);
    if (IMPORT_DONE.includes(session?.status)) return session;
  }
  return session;
};

// Add manually-entered contacts to a list: create each (reusing the existing
// contact when the number is already in the tenant), then attach by id.
// Returns { ids, failed } — rows that could not be resolved don't block the rest.
export const addNewContacts = async (listId, contacts) => {
  const ids = [];
  const failed = [];
  for (const c of contacts) {
    const mobile = toE164(c.mobile);
    try {
      const created = unwrapEnvelope(
        (await axiosApi.post("contacts", {
          firstName: c.firstName,
          lastName: c.lastName || undefined,
          mobile,
          email: c.email || undefined,
        })).data
      );
      if (created?.id) ids.push(created.id);
    } catch (error) {
      if (error?.response?.status === 409) {
        const existing = await get(`contacts/mobile/${encodeURIComponent(mobile)}`).catch(() => null);
        if (existing?.id) {
          ids.push(existing.id);
          continue;
        }
      }
      failed.push({ contact: c, message: apiErrorMessage(error) });
    }
  }
  if (ids.length) {
    await axiosApi.post(`contacts/lists/${listId}/contacts`, ids); // body = Long[]
  }
  return { ids, failed };
};

// Which lists is this msisdn in? GET /api/contacts/mobile/{mobile}/lists
export const getContactByMsisdn = (msisdn) =>
  get(`contacts/mobile/${encodeURIComponent(msisdn)}/lists`);

// Which lists is this contact in, keyed by id? GET /api/contacts/{id}/lists
// Preferred over the by-mobile path (no MSISDN-normalization mismatch) — mirrors
// the reference portal's listsForContact.
export const getContactListsById = (contactId) =>
  get(`contacts/${contactId}/lists`);

// Soft-delete a contact (removes from all lists). DELETE /api/contacts/{id}
export const deleteContact = (contactId) => del(`contacts/${contactId}`);

// Remove a contact from ONE list. DELETE /api/contacts/lists/{listId}/contacts/{contactId}
export const removeContactFromList = (listId, contactId) =>
  del(`contacts/lists/${listId}/contacts/${contactId}`);

// Add one contact to multiple lists. POST /api/contacts/{id}/lists  (body = Long[])
export const addContactToMultipleLists = async ({ contactId, listIds }) => {
  const res = await axiosApi.post(
    `contacts/${contactId}/lists`,
    (listIds || []).map(Number)
  );
  return unwrapEnvelope(res.data);
};

// Set / clear a contact's expiry. PUT /api/contacts/{id}/expiry
export const updateContactExpiry = (contactId, expiryDate) =>
  put(`contacts/${contactId}/expiry`, { expiryDate });

// Group-lead list ACL (admin-only). GET /api/contacts/users/{userId}/lists
export const getUserAssignedLists = (userId) => get(`contacts/users/${userId}/lists`, ADMIN_OPT);

// PUT /api/contacts/users/{userId}/lists { listIds }
export const assignUserToLists = ({ user_id, contact_list_id }) => {
  return axiosApi
    .put(`contacts/users/${user_id}/lists`, { listIds: (contact_list_id || []).map(Number) }, ADMIN_OPT)
    .then((r) => unwrapEnvelope(r?.data));
};
